// Boolean feature flags driven by Vite env vars. Reads happen at build time,
// so flipping a flag requires a redeploy in environments where the value
// comes from `.env.<mode>`.
//
// Adding a flag:
//   1. Add a key to FLAG_DEFAULTS below with its default boolean.
//   2. (Optional) Override per environment by setting
//      VITE_FEATURE_<UPPER_SNAKE_NAME>=true|false in that env's `.env` file.
//   3. Read it via `useFeatureFlag('flag_name')` inside components or
//      `isFeatureEnabled('flag_name')` outside of React.
//
// The registry is intentionally typed: `useFeatureFlag('typo')` is a compile
// error when 'typo' isn't a registered flag, which catches stale references
// when a flag is removed.

const FLAG_DEFAULTS = {
  crm: false,
} satisfies Record<string, boolean>;

export type FeatureFlag = keyof typeof FLAG_DEFAULTS;

const TRUTHY = new Set(['true', '1', 'yes', 'on']);

const envKey = (name: string): string => `VITE_FEATURE_${name.toUpperCase()}`;

const parseEnvValue = (raw: string | undefined, fallback: boolean): boolean => {
  if (raw === undefined || raw === '') return fallback;
  return TRUTHY.has(raw.trim().toLowerCase());
};

export const isFeatureEnabled = (name: FeatureFlag): boolean => {
  const raw = import.meta.env[envKey(name)] as string | undefined;
  return parseEnvValue(raw, FLAG_DEFAULTS[name]);
};

// Hook form is a thin wrapper today; keeping the indirection lets us swap in a
// dynamic source (e.g. account-level flags from /users/current) later without
// changing call sites.
export const useFeatureFlag = (name: FeatureFlag): boolean => isFeatureEnabled(name);
